// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/coverPickAhead.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/coverPickAhead.js
//
// What a new invitation starts looking like: a random cover template, and
// about half the time a video theme behind it (the rest of the time the
// blurred cover is the background) - different every time Create opens.
//
// ── Why "pick ahead" ───────────────────────────────────────────────────────
//
// Choosing at the moment Create opens would mean a database query and a
// download before anything shows: a blank cover, then a pop. So the choice
// is made one open AHEAD. The next look is picked and downloaded in the
// background - first when the app (or site) starts - and simply shown the
// next time Create opens, instantly. Opening Create hands it over and starts
// preparing the one after. At most two are kept: the one on screen and the
// next, each replaced as it is used, so the space used stays small.
//
// This module is only the choosing; each platform stores and downloads:
//   app      src/features/create/api/coverPickAhead.js  (files on the device)
//   website  src/services/coverPickAhead.js             (the browser's Cache Storage)

/** The share of looks that come with a video theme. */
export const PICK_VIDEO_SHARE = 0.5;

/** Columns the pools are read with. */
export const TEMPLATE_POOL_COLUMNS = 'id, webp_url, thumbnail_url, event_type';
export const THEME_POOL_COLUMNS = 'id, theme_name, video_cdn_url, thumbnail_cdn_url, default_color';

/** Upper bounds on the pools read; plenty for variety, small to fetch. */
export const TEMPLATE_POOL_LIMIT = 300;
export const THEME_POOL_LIMIT = 60;

const pickFrom = (list, avoid, random) => {
  const fresh = list.filter((x) => !avoid.includes(x.id));
  const from = fresh.length ? fresh : list;
  return from[Math.min(from.length - 1, Math.floor(random() * from.length))];
};

/**
 * The next look.
 *
 * @param {object} p
 * @param {Array<{id, webp_url, thumbnail_url, event_type?}>} p.templates  invitation cover templates
 * @param {Array<{id, theme_name, video_cdn_url, thumbnail_cdn_url, default_color}>} [p.themes]
 * @param {Array<object|null>} [p.avoid]  looks already on hand (on screen, ready) - not repeated when there is a choice
 * @param {boolean} [p.allowVideo]  false to leave video out (e.g. on mobile data)
 * @param {() => number} [p.random]
 * @returns {null | {
 *   id: string,
 *   template: {id, webpUrl, thumbnailUrl, eventType},
 *   video: null | {id, themeName, videoCdnUrl, thumbnailCdnUrl, defaultColor},
 * }}
 */
export function choosePick({ templates, themes = [], avoid = [], allowVideo = true, random = Math.random }) {
  const usableTemplates = (templates || []).filter((t) => t?.id && t.webp_url);
  if (usableTemplates.length === 0) return null;
  const usableThemes = (themes || []).filter((t) => t?.id && t.video_cdn_url);

  const avoidTemplates = avoid.filter(Boolean).map((p) => p.template?.id);
  const avoidThemes = avoid.filter(Boolean).map((p) => p.video?.id).filter(Boolean);

  const t = pickFrom(usableTemplates, avoidTemplates, random);
  const withVideo = allowVideo && usableThemes.length > 0 && random() < PICK_VIDEO_SHARE;
  const v = withVideo ? pickFrom(usableThemes, avoidThemes, random) : null;

  return {
    id: `${t.id}:${v ? v.id : '-'}`,
    template: {
      id: t.id,
      webpUrl: t.webp_url,
      thumbnailUrl: t.thumbnail_url || null,
      eventType: t.event_type || null,
    },
    video: v
      ? {
        id: v.id,
        themeName: v.theme_name || null,
        videoCdnUrl: v.video_cdn_url,
        thumbnailCdnUrl: v.thumbnail_cdn_url || null,
        defaultColor: v.default_color || null,
      }
      : null,
  };
}
